import { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Checkbox,
  Divider,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { coachPortalApi } from '../../../services/api/coachPortal';
import type { Coach, UpdateCoachRequest } from '../../../types';
import { TimezoneSelect } from '../../../components/common';
import { revealField } from '../../../components/coaches/meetingLinkPrompt';
import {
  RACE_ETHNICITY_OPTIONS,
  GENDER_IDENTITY_OPTIONS,
  PRONOUNS_OPTIONS,
  INDUSTRY_OPTIONS,
  CERTIFICATION_OPTIONS,
  LEARNING_EXPERIENCE_OPTIONS,
  MODALITY_PROFICIENCY_OPTIONS,
  PREDEFINED_EXPERTISE,
} from '../../../config/coachConfig';
import {
  COACH_EMAIL_MESSAGES,
  coachEmailErrorField,
  isMedleyEmail,
  medleyEmailOf,
  normalizeEmailForCompare,
  validatePersonalEmail,
} from '../../../utils/medleyEmail';

// MED-1365 QA round 8: longer than the Modal's 200 ms open animation.
const REVEAL_AFTER_OPEN_MS = 250;

interface EditCoachProfileModalProps {
  /** MED-1365 QA round 6: the meeting link field to focus when the window opens. */
  focusField?: 'zoom_link' | 'teams_link' | 'google_meet_link' | null;
  opened: boolean;
  onClose: () => void;
  coach: Coach;
  onSaved: (coach: Coach) => void;
}

export function EditCoachProfileModal({
  opened,
  onClose,
  coach,
  onSaved,
  focusField = null,
}: EditCoachProfileModalProps) {
  const [saving, setSaving] = useState(false);

  const form = useForm<UpdateCoachRequest>({
    // MED-1355: check the personal email when it loses focus.
    validateInputOnBlur: ['preferred_email'],
    initialValues: {
      first_name: '',
      last_name: '',
      phone: '',
      preferred_email: '',
      bio: '',
      coaching_specializations: '',
      location: '',
      home_base_timezone: '',
      timezone_preference: '',
      pronouns: '',
      gender_identity: '',
      race_ethnicity: [],
      industry_experience: [],
      certifications: [],
      expertise_areas: [],
      modality_proficiencies: [],
      preferred_learning_experiences: [],
      coaching_topics_of_interest: '',
      zoom_link: '',
      teams_link: '',
      google_meet_link: '',
    },
    validate: {
      first_name: (value) => (value && value.trim() ? null : 'First name is required'),
      last_name: (value) => (value && value.trim() ? null : 'Last name is required'),
      // MED-1355: the same personal email rule as the API. An unchanged value
      // is not sent and not checked, so a coach with no personal email yet,
      // or an older Medley address there, can still save other fields.
      // The coach cannot edit the Medley email, so the Medley address
      // message asks for their own address instead.
      preferred_email: (value) => {
        if (normalizeEmailForCompare(value) === normalizeEmailForCompare(coach?.preferred_email)) {
          return null;
        }
        const error = validatePersonalEmail(value);
        return error === COACH_EMAIL_MESSAGES.personal
          ? 'This is a Medley address. Enter your own address.'
          : error;
      },
      zoom_link: (value) =>
        value && value.trim() && !value.trim().startsWith('https://')
          ? 'Must be a valid HTTPS URL'
          : null,
      teams_link: (value) =>
        value && value.trim() && !value.trim().startsWith('https://')
          ? 'Must be a valid HTTPS URL'
          : null,
      google_meet_link: (value) =>
        value && value.trim() && !value.trim().startsWith('https://')
          ? 'Must be a valid HTTPS URL'
          : null,
    },
  });

  useEffect(() => {
    if (opened && coach) {
      form.setValues({
        first_name: coach.first_name || '',
        last_name: coach.last_name || '',
        phone: coach.phone || '',
        preferred_email: coach.preferred_email || '',
        bio: coach.bio || '',
        coaching_specializations: coach.coaching_specializations || '',
        location: coach.location || '',
        home_base_timezone: coach.home_base_timezone || '',
        timezone_preference: coach.timezone_preference || '',
        pronouns: coach.pronouns || '',
        gender_identity: coach.gender_identity || '',
        race_ethnicity: coach.race_ethnicity || [],
        industry_experience: coach.industry_experience || [],
        certifications: coach.certifications || [],
        expertise_areas: coach.expertise_areas || [],
        modality_proficiencies: coach.modality_proficiencies || [],
        preferred_learning_experiences: coach.preferred_learning_experiences || [],
        coaching_topics_of_interest: coach.coaching_topics_of_interest || '',
        zoom_link: coach.zoom_link || '',
        teams_link: coach.teams_link || '',
        google_meet_link: coach.google_meet_link || '',
      });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, coach]);

  const handleSubmit = async (values: UpdateCoachRequest) => {
    try {
      setSaving(true);
      // MED-1355: send the personal email only when it changed.
      const payload: UpdateCoachRequest = { ...values };
      if (
        normalizeEmailForCompare(payload.preferred_email) ===
        normalizeEmailForCompare(coach.preferred_email)
      ) {
        delete payload.preferred_email;
      }
      const updated = await coachPortalApi.updateProfile(payload);
      notifications.show({
        title: 'Success',
        message: 'Profile updated successfully',
        color: 'green',
      });
      onSaved(updated);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update profile';
      if (coachEmailErrorField(error) === 'preferred_email') {
        form.setFieldError('preferred_email', message);
        return;
      }
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // MED-1365 QA round 7: the link fields, so a prompt can scroll to one.
  const linkFields = useRef<Partial<Record<NonNullable<typeof focusField>, HTMLInputElement | null>>>(
    {}
  );

  // QA round 8: the Needs Attention link opens this window as the page first
  // renders, so Mantine runs no enter transition and onEnterTransitionEnd
  // never fires. Reveal the field after the open animation on every open.
  useEffect(() => {
    if (!opened || !focusField) return;
    const timer = window.setTimeout(
      () => revealField(linkFields.current[focusField]),
      REVEAL_AFTER_OPEN_MS
    );
    return () => window.clearTimeout(timer);
  }, [opened, focusField]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Profile"
      size="lg"
      closeOnClickOutside={false}
    >
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Basic Information */}
          <Text size="sm" fw={600} c="dimmed" tt="uppercase">
            Basic Information
          </Text>
          <Group grow>
            <TextInput
              label="First Name"
              required
              {...form.getInputProps('first_name')}
            />
            <TextInput
              label="Last Name"
              required
              {...form.getInputProps('last_name')}
            />
          </Group>
          <TextInput
            label="Phone"
            placeholder="(555) 555-5555"
            {...form.getInputProps('phone')}
          />
          {/* MED-1355: the coach edits the personal email only. The hint
              sits under the input. The Medley email shows read-only. */}
          {/* QA round 5: an older Medley address still stored here gets a
              warning in place of the hint. It does not block other edits,
              because an unchanged value is not sent. */}
          <TextInput
            label="Personal email"
            placeholder="e.g., sarah@leadershiplab.co"
            description={
              isMedleyEmail(form.values.preferred_email)
                ? COACH_EMAIL_MESSAGES.storedPersonalIsMedley
                : 'Your own address, not a withmedley.com one.'
            }
            inputWrapperOrder={['label', 'input', 'description', 'error']}
            styles={{
              description: {
                marginTop: 4,
                color: isMedleyEmail(form.values.preferred_email)
                  ? 'var(--medley-text-warning)'
                  : 'var(--medley-text-secondary)',
              },
            }}
            withAsterisk
            {...form.getInputProps('preferred_email')}
          />
          <div>
            <Text size="sm" fw={500}>
              Medley email
            </Text>
            {medleyEmailOf(coach.email) ? (
              <Text size="sm">{coach.email}</Text>
            ) : (
              <Text size="sm" c="var(--medley-text-tertiary)">
                Not set
              </Text>
            )}
            <Text size="xs" c="var(--medley-text-secondary)" mt={4}>
              Set by the Medley team.
            </Text>
          </div>
          <TextInput
            label="Coaching Specializations"
            placeholder="e.g., Executive coaching, Leadership development"
            {...form.getInputProps('coaching_specializations')}
          />
          <Textarea
            label="Bio"
            placeholder="Tell us about yourself..."
            minRows={3}
            autosize
            {...form.getInputProps('bio')}
          />

          <Divider />

          {/* Location & Timezone */}
          <Text size="sm" fw={600} c="dimmed" tt="uppercase">
            Location & Timezone
          </Text>
          <TextInput
            label="Location"
            placeholder="e.g., Atlanta, GA"
            {...form.getInputProps('location')}
          />
          <TimezoneSelect
            label="Home Base Timezone"
            placeholder="Select timezone"
            {...form.getInputProps('home_base_timezone')}
          />
          <TimezoneSelect
            label="Current Timezone Preference"
            placeholder="Same as home base"
            clearable
            {...form.getInputProps('timezone_preference')}
          />

          <Divider />

          {/* Professional Background */}
          <Text size="sm" fw={600} c="dimmed" tt="uppercase">
            Professional Background
          </Text>
          <MultiSelect
            label="Industry Experience"
            placeholder="Select industries"
            data={[...INDUSTRY_OPTIONS]}
            searchable
            {...form.getInputProps('industry_experience')}
          />
          <MultiSelect
            label="Certifications"
            placeholder="Select certifications"
            data={[...CERTIFICATION_OPTIONS]}
            {...form.getInputProps('certifications')}
          />
          <MultiSelect
            label="Expertise Areas"
            placeholder="Select areas of expertise"
            data={[...PREDEFINED_EXPERTISE]}
            searchable
            {...form.getInputProps('expertise_areas')}
          />
          <Checkbox.Group
            label="Modality Proficiencies"
            description="Select modalities you are proficient in"
            {...form.getInputProps('modality_proficiencies')}
          >
            <Group mt="xs">
              {MODALITY_PROFICIENCY_OPTIONS.map((option) => (
                <Checkbox key={option.value} value={option.value} label={option.label} />
              ))}
            </Group>
          </Checkbox.Group>

          <Divider />

          {/* Learning & Development */}
          <Text size="sm" fw={600} c="dimmed" tt="uppercase">
            Learning & Development
          </Text>
          <MultiSelect
            label="Preferred Learning Experiences"
            placeholder="Select preferences"
            data={[...LEARNING_EXPERIENCE_OPTIONS]}
            {...form.getInputProps('preferred_learning_experiences')}
          />
          <Textarea
            label="Coaching Topics of Interest"
            placeholder="Topics you'd like to explore..."
            minRows={2}
            autosize
            {...form.getInputProps('coaching_topics_of_interest')}
          />

          <Divider />

          {/* Identity */}
          <Text size="sm" fw={600} c="dimmed" tt="uppercase">
            Identity (Optional)
          </Text>
          <Select
            label="Pronouns"
            placeholder="Select pronouns"
            data={[...PRONOUNS_OPTIONS]}
            clearable
            {...form.getInputProps('pronouns')}
          />
          <Select
            label="Gender Identity"
            placeholder="Select gender identity"
            data={[...GENDER_IDENTITY_OPTIONS]}
            clearable
            {...form.getInputProps('gender_identity')}
          />
          <MultiSelect
            label="Race/Ethnicity"
            placeholder="Select all that apply"
            data={[...RACE_ETHNICITY_OPTIONS]}
            {...form.getInputProps('race_ethnicity')}
          />

          <Divider />

          {/* Session Settings */}
          <Text size="sm" fw={600} c="dimmed" tt="uppercase">
            Meeting Links
          </Text>
          <TextInput
            label="Zoom Link"
            placeholder="https://zoom.us/j/..."
            data-autofocus={focusField === 'zoom_link' || undefined}
            ref={(el) => {
              linkFields.current.zoom_link = el;
            }}
            {...form.getInputProps('zoom_link')}
          />
          <TextInput
            label="Teams Link"
            placeholder="https://teams.microsoft.com/..."
            data-autofocus={focusField === 'teams_link' || undefined}
            ref={(el) => {
              linkFields.current.teams_link = el;
            }}
            {...form.getInputProps('teams_link')}
          />
          <TextInput
            label="Google Meet Link"
            placeholder="https://meet.google.com/..."
            data-autofocus={focusField === 'google_meet_link' || undefined}
            ref={(el) => {
              linkFields.current.google_meet_link = el;
            }}
            {...form.getInputProps('google_meet_link')}
          />

          {/* Footer */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
